<template>
  <div class="dashboard__card" v-cloak>
    <CardLoading v-if="loaders.dashboardData"></CardLoading>
    <div class="dashboard__card-content" :class="{ 'loadingBlur': loaders.dashboardData }" v-if="hasAudiologyKPIs" v-cloak>
      <div class="dashboard__card-header">
        <h2 class="dashboard__card-title">Audiology KPIs</h2>
        <span class="dashboard__card-title-addendum">{{ dateRange }}</span>
        <div class="dashboard__card-title-icon dashboard__card-title-icon--audio-kpi"><i class="fa fa-chart-bar"></i></div>
      </div>
      <div class="row dashboard__card-body dashboard__card-body--bordered" v-if="yearPrior.year" v-cloak>
        <div class="dashboard__grid dash-audiology-kpi__content-container">
          <div class="col-sm-6 dashboard__card-body-col dash-audiology-kpi__content-col-container">
            <div class="dash-audiology-kpi__year">
              <div class="dash-audiology-kpi__date" v-if="fiscalYear">{{ yearPrior.year }}</div>
              <div class="dash-audiology-kpi__date" v-else>{{ yearPrior.year }} / {{ yearCurrent.year }}</div>
              <span>YTD</span>
            </div>
            <div class="dash-audiology-kpi__category">Net revenue</div>
            <div class="dash-audiology-kpi__value">{{ currency(yearPrior.net_revenue, '$') }}</div>
            <div class="dash-audiology-kpi__category">Net profit</div>
            <div class="dash-audiology-kpi__value">{{ currency(yearPrior.net_profit, '$') }}</div>
            <div class="dash-audiology-kpi__category">Net units</div>
            <div class="dash-audiology-kpi__value">{{ yearPrior.net_units }}</div>
            <div class="dash-audiology-kpi__category">HAE appts</div>
            <div class="dash-audiology-kpi__value">{{ yearPrior.hae_appts }}</div>
            <div class="dash-audiology-kpi__category">Patients purch</div>
            <div class="dash-audiology-kpi__value">{{ yearPrior.patient_purchases }}</div>
            <div class="dash-audiology-kpi__category">Net ASP</div>
            <div class="dash-audiology-kpi__value">{{ currency(yearPrior.net_asp, '$') }}</div>
          </div>
          <div class="col-sm-6 dashboard__card-body-col dash-audiology-kpi__content-col-container">
            <div class="dash-audiology-kpi__year">
              <div class="dash-audiology-kpi__date">{{ yearCurrent.year }}</div>
              <span>YTD</span>
            </div>
            <div class="dash-audiology-kpi__category">Net revenue</div>
            <div class="dash-audiology-kpi__value">{{ currency(yearCurrent.net_revenue, '$')  }}</div>
            <div class="dash-audiology-kpi__category">Net profit</div>
            <div class="dash-audiology-kpi__value">{{ currency(yearCurrent.net_profit, '$') }}</div>
            <div class="dash-audiology-kpi__category">Net units</div>
            <div class="dash-audiology-kpi__value">{{ yearCurrent.net_units }}</div>
            <div class="dash-audiology-kpi__category">HAE appts</div>
            <div class="dash-audiology-kpi__value">{{ yearCurrent.hae_appts }}</div>
            <div class="dash-audiology-kpi__category">Patients purch</div>
            <div class="dash-audiology-kpi__value">{{ yearCurrent.patient_purchases }}</div>
            <div class="dash-audiology-kpi__category">Net ASP</div>
            <div class="dash-audiology-kpi__value">{{ currency(yearCurrent.net_asp, '$') }}</div>
          </div>
        </div>
      </div>
      <div class="row dashboard__card-body" v-else>
        <div class="alert alert-warning">
          <div class="dashboard__no-data">No KPI Data Available</div>
        </div>
      </div>
      <div class="dashboard__card-footer">
        <div class="dashboard__card-link">
          <router-link to="/audiology-kpis" class="dashboard__card-link-text">View All</router-link>
        </div>
      </div>
    </div>
    <div class="dashboard__audio-kpis__teaser" :class="{ 'loadingBlur': loaders.dashboardData }" v-cloak v-else>
      <div class="dashboard__audio-kpis__teaser--bg"></div>
      <div class="dashboard__audio-kpis__teaser--content" v-cloak>
        Please speak with your Regional Manager about how tracking KPIs can work for your practice.
      </div>
    </div>
  </div>

</template>

<script>
  import moment from 'moment';
  import { mapGetters } from 'vuex';
  import CardLoading from './CardLoading.vue';
  import DataMethods from '../../utils/DataMethods';

  export default {
    name: 'AudiologyKpisCard',
    mixins: [DataMethods],
    components: {
      CardLoading,
    },
    async created() {
      await this.$store.dispatch('audiologyKpis/getAudiologyKpisYears');
      await this.$store.dispatch('audiologyKpis/getAudiologyKpisCardData');
    },
    computed: {
      ...mapGetters('audiologyKpis', [
        'loaders',
        'metrics',
        'dashboardData',
      ]),
      fiscalYear() {
        return this.dashboardData.yearStart === 1;
      },
      month() {
        return this.dashboardData.month;
      },
      year() {
        return this.dashboardData.year;
      },
      curYear() {
        return this.dashboardData.curYear;
      },
      prevYear() {
        return this.dashboardData.prevYear;
      },
      dateRange() {
        if (this.month) {
          return `Jan 1 - ${moment().month(this.month - 1).endOf('month').format('MMM DD')}`;
        }
        return '';
      },
      yearPrior() {
        if (!this.prevYear || this.prevYear.length === 0) {
          return null;
        }
        return {
          year: (this.year - 1),
          net_revenue: this.getYtdAdjusted(this.prevYear, 'net_ha_revenue', this.month),
          net_profit: this.getYtdAdjusted(this.prevYear, 'net_profit', this.month),
          net_units: this.getYtdAdjusted(this.prevYear, 'net_units_sold', this.month).toLocaleString(),
          hae_appts: this.getYtdAdjusted(this.prevYear, 'number_ha_appointments', this.month).toLocaleString(),
          patient_purchases: this.getYtdAdjusted(this.prevYear, 'number_patients_purchased', this.month).toLocaleString(),
          net_asp: this.getYtdAdjusted(this.prevYear, 'avg_selling_price_post_disc', this.month),
        };
      },
      yearCurrent() {
        if (!this.curYear || this.curYear.length === 0) {
          return null;
        }
        return {
          year: this.year,
          net_revenue: this.getYtdAdjusted(this.curYear, 'net_ha_revenue', this.month),
          net_profit: this.getYtdAdjusted(this.curYear, 'net_profit', this.month),
          net_units: this.getYtdAdjusted(this.curYear, 'net_units_sold', this.month).toLocaleString(),
          hae_appts: this.getYtdAdjusted(this.curYear, 'number_ha_appointments', this.month).toLocaleString(),
          patient_purchases: this.getYtdAdjusted(this.curYear, 'number_patients_purchased', this.month).toLocaleString(),
          net_asp: this.getYtdAdjusted(this.curYear, 'avg_selling_price_post_disc', this.month),
        }
      },
      hasAudiologyKPIs() {
        return !this.loaders.dashboardData && this.yearPrior && this.yearCurrent;
      },
    },
    methods: {
      /**
       *  Same as getYtd below, except will average the sum of aggregate metrics.
       *  Other functions rely on getYtd to provide a SUM, even if the aggregate type is "avg".
       *  Some examples are calcTotals() and getCalculatedYtd().
       *  Furthermore, it might make sense to avoid duplication and just call getYtd from this method
       *  to get a SUM, then divide. However, the division doesn't happen for all "avg" aggregate types.
       *  It only applies to those that aren't pre-calculated. Thus getCalc would have to return both a
       *  summed value and a boolean indicating whether the SUM was pre-calculated or not.
       *  To avoid having to return an array, I'm just duplicating the code here.
       */
      getYtdAdjusted(yearObj, field, throughMonth, offset) {
        if (throughMonth == undefined) { throughMonth = false; }
        if (offset == undefined) { offset = 0; }
        // Check if this field is one with a special calculation.
        let calcValue = this.getCalculatedYtd(yearObj, field, throughMonth);
        if (calcValue !== false) {
          return calcValue;
        }

        // Otherwise assume it's a simple field that just needs to summed up.
        let sum = 0;
        for (let i = offset, len = yearObj.length; i < len; i++) {
          if (throughMonth == false || throughMonth > i) {
            if (yearObj[i][field] != undefined && yearObj[i][field] != null) {
              sum += parseFloat(yearObj[i][field]);
            }
          }
        }

        // Check if this YTD value is an average rather than sum
        let metric = this.metrics.find(function(metric) { return metric.field == field; })
        if (metric.aggregateType == 'avg') {
          sum = sum / len;
        }
        return sum;
      },

      /**
       *  Given and array of KPI month objects, a field to look at, and an optional throughMonth,
       *  Returns the YTD value of that field if its a calculated property, and false otherwise.
       *
       *  @param yearObj An array of KPI month objects.
       *  @param field A string denoting a KPI to look at.
       *  @param throughMonth (optional), A number between 1 and 12.
       */
      getCalculatedYtd(yearObj, field, throughMonth) {
        if (throughMonth == undefined) {
          throughMonth = false;
        }
        if (field == 'avg_selling_price_post_disc') {
          return this.getRatio(this.getYtd(yearObj, 'net_ha_revenue', throughMonth), this.getYtd(yearObj, 'net_units_sold', throughMonth), 0);
        }
        return false;
      },

      /**
       *  Give aggregate totals of a KPI. If given through month, will limit sum
       *  from January through the month given, otherwise sums all available months.
       *
       *  @param yearObj A KPI report in json format.
       *  @param field The KPI to sum denoted by it's field name.
       *  @param throughMonth A month to limit the summation to. Valid numbers are 1-12.
       *  @param offset Offset to start at.
       *  @return A numeric value.
       */
      getYtd(yearObj, field, throughMonth, offset) {
        if (throughMonth == undefined) { throughMonth = false; }
        if (offset == undefined) { offset = 0; }
        // Check if this field is one with a special calculation.
        let calcValue = this.getCalculatedYtd(yearObj, field, throughMonth);
        if (calcValue !== false) {
          return calcValue;
        }

        // Otherwise assume it's a simple field that just needs to summed up.
        let sum = 0;
        for (let i = offset, len = yearObj.length; i < len; i++) {
          if (throughMonth == false || throughMonth > i) {
            if (yearObj[i][field] != undefined && yearObj[i][field] != null) {
              sum += parseFloat(yearObj[i][field]);
            }
          }
        }

        // Check if this YTD value is an average rather than sum
        // var metric = this.metrics.find(function(metric) { return metric.field == field; })
        // if (metric.aggregateType == 'avg') {
        //     sum = sum / len;
        // }
        return sum;
      },

      /**
       *  Method that helps avoid dividing by zero.
       *
       *  @param numerator A numeric value.
       *  @param denominator A numeric value.
       *  @param returnValueIfDenominatorIsInvalid Optional value to return if division by zero occurs.
       *  @return A ratio.
       */
      getRatio(numerator, denominator, returnValueIfDenominatorIsInvalid = false) {
        if (this.empty(parseFloat(denominator))) {
          return (returnValueIfDenominatorIsInvalid !== false) ? returnValueIfDenominatorIsInvalid : '';
        }
        if (this.empty(parseFloat(numerator))) {
          return 0;
        }
        return numerator / denominator;
      },

      /**
       *  Emulates PHP's empty() method.
       *
       *  @param data (mixed) A variable you want to determine if its empty.
       *  @return boolean
       */
      empty(data) {
        // If data is a number
        if (typeof (data) == 'number' || typeof (data) == 'boolean') {
          return (data == 0);
        }
        // If data is a string literally representing zero
        if (data == '0') {
          return true;
        }
        // All other cases (functions, regular expressions, elements, documents, etc)
        return lodash.isEmpty(data);
      },

      // toCurrency(v, prefix) {
      //   if (prefix == undefined) { prefix = ''; }
      //   var n = v,
      //     c = isNaN(c = Math.abs(c)) ? 0 : c,
      //     d = d == undefined ? "." : d,
      //     t = t == undefined ? "," : t,
      //     i = parseInt(n = Math.abs(+n || 0).toFixed(c)) + "",
      //     j = (j = i.length) > 3 ? j % 3 : 0;
      //   var result = (j ? i.substr(0, j) + t : "") + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + t) + (c ? d + Math.abs(n - i).toFixed(c).slice(2) : "");
      //   if (result == '0') {
      //     var s = '';
      //   } else {
      //     //console.log(v);
      //     var s = (v < 0) ? "-" : "";
      //   }
      //
      //   return s + prefix + result;
      // },
    },
  };
</script>
